import { NavigationMenu } from '@base-ui/react/navigation-menu';
import cn from 'classnames';
import { useNavUI } from '@context/MainNavContext/NavigationState';
import { useLocale } from '@hooks/useLocale';
import {
  MegaMenuLevels,
  trackMegaMenuNavigation,
} from '_utils/GA4/trackMegaMenuEvents';
import styles from '../MegaMenu.module.scss';
import type { MenuItem } from '../types';
import { Link } from '../Link';
import { MenuContent } from '../MenuContent';
import { menuDataByLocale } from '../menuData';

interface MegaMenuDesktopProps {
  items?: MenuItem[];
  onOpenChange?: (open: boolean) => void;
}

export default function MegaMenuDesktop({
  items,
  onOpenChange,
}: MegaMenuDesktopProps) {
  const locale = useLocale();
  const { headerHeight } = useNavUI();
  const defaultMenuData =
    menuDataByLocale[locale ?? 'en'] ?? menuDataByLocale.en;
  const navItems = items ?? (defaultMenuData as MenuItem[]);
  return (
    <NavigationMenu.Root
      className={styles.Root}
      delay={9999999}
      closeDelay={50}
      onValueChange={(value) => {
        onOpenChange?.(value !== '');
        if (value) {
          const item = navItems.find((i) => i.label === value);
          if (item) {
            trackMegaMenuNavigation({
              level: MegaMenuLevels.level_1,
              linkUrl: null,
              linkText: item.label,
              menuPath: item.label,
            });
          }
        }
      }}
    >
      <NavigationMenu.List className={styles.List}>
        {navItems.map((item) => (
          <NavigationMenu.Item key={item.label} value={item.label}>
            {item.url && !item.linkColumns ? (
              <Link
                className={cn(styles.Trigger, {
                  [styles['Trigger--highlight']]: item.highlight,
                })}
                href={item.url}
                onClick={() =>
                  trackMegaMenuNavigation({
                    level: MegaMenuLevels.level_1,
                    linkUrl: item.url,
                    linkText: item.label,
                    menuPath: item.label,
                  })
                }
              >
                {item.label}
              </Link>
            ) : (
              <>
                <NavigationMenu.Trigger
                  className={cn(styles.Trigger, {
                    [styles['Trigger--highlight']]: item.highlight,
                  })}
                >
                  {item.label}
                </NavigationMenu.Trigger>
                {item.linkColumns && (
                  <NavigationMenu.Content
                    keepMounted
                    className={cn(styles.Content, {
                      [styles.ContentFeatureLeft]:
                        item.featureCard?.layoutPosition === 'Left',
                    })}
                  >
                    <MenuContent item={item} />
                  </NavigationMenu.Content>
                )}
              </>
            )}
          </NavigationMenu.Item>
        ))}
      </NavigationMenu.List>

      <NavigationMenu.Portal keepMounted>
        <NavigationMenu.Backdrop className={styles.Backdrop} />
        <div className={styles.ViewportWrapper} style={{ top: headerHeight }}>
          <NavigationMenu.Positioner
            className={styles.Positioner}
            sticky
            collisionAvoidance={{
              side: 'none',
              align: 'none',
              fallbackAxisSide: 'none',
            }}
          >
            <NavigationMenu.Popup className={styles.Popup}>
              <NavigationMenu.Viewport className={styles.Viewport} />
            </NavigationMenu.Popup>
          </NavigationMenu.Positioner>
        </div>
      </NavigationMenu.Portal>
    </NavigationMenu.Root>
  );
}
